import React from 'react'

import { useAPIGetContentSearchResults } from 'api/content-search/useAPIContentSearch'
import useSearchQuery from './useSearchQuery'
import { useContentSearchFilter } from 'stores/content-search'

const useSearchResults = () => {
  const [lastPageNumber, setLastPageNumber] = React.useState<number>(0)
  const { selectedFormats, selectedTopics } = useContentSearchFilter()
  const { searchQueryFromURL } = useSearchQuery()
  const { data, ...rest } = useAPIGetContentSearchResults({
    keyword: searchQueryFromURL,
    params: { limit: 10, formats: selectedFormats, topics: selectedTopics },
  })

  const searchResultsData = React.useMemo(() => {
    if (rest.isLoading) return []
    return data?.pages.flatMap((page) => page.data) ?? []
  }, [data?.pages, rest.isLoading])

  const searchResultsMeta = React.useMemo(() => {
    if (rest.isLoading) return []
    return data?.pages?.[(data?.pages ?? []).length - 1]?.meta ?? {}
  }, [data?.pages, rest.isLoading])

  const hasSearchResultsData = React.useMemo(
    () => Boolean(searchResultsData),
    [searchResultsData]
  )

  const searchResultsCount = React.useMemo(
    () => data?.pages?.[0]?.meta?.total ?? 0,
    [data?.pages]
  )

  React.useEffect(() => {
    if (!searchResultsMeta?.isNextPageError) {
      const { offset, limit } = searchResultsMeta
      const currentPageNumber = offset / limit + 1
      setLastPageNumber(currentPageNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsMeta])

  return {
    ...rest,
    hasSearchResultsData,
    searchResultsData,
    searchResultsCount,
    searchResultsMeta,
    lastPageNumber,
  }
}

export default useSearchResults
